// Top-down chassis visualizer. Exaggerates camber (tilt) & toe (rotation) so the
// driver can read the alignment at a glance. Colors reflect pass/warn/fail status.
import { STATUS_META } from "../lib/specs";

const num = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

function Wheel({ x, y, camber, toe, side, status }) {
  const meta = STATUS_META[status] || STATUS_META.empty;
  const c = num(camber);
  const t = num(toe);
  // toe-in rotates the top of the wheel inward. Left vs right mirrored.
  const toeDeg = t === null ? 0 : Math.max(-12, Math.min(12, t * 40)) * (side === "left" ? 1 : -1);
  // camber squishes width (visual tilt). Positive camber = top leans out.
  const camScale = c === null ? 1 : Math.max(0.45, 1 - Math.abs(c) * 0.45);

  return (
    <g transform={`translate(${x} ${y}) rotate(${toeDeg})`}>
      <rect
        x={-11 * camScale}
        y={-26}
        width={22 * camScale}
        height={52}
        rx={5}
        fill={meta.bg}
        stroke={meta.color}
        strokeWidth={2.5}
      />
      {/* tread lines */}
      {[-16, -6, 4, 14].map((ty) => (
        <line key={ty} x1={-7 * camScale} y1={ty} x2={7 * camScale} y2={ty} stroke={meta.color} strokeWidth={1} opacity={0.5} />
      ))}
    </g>
  );
}

export const WheelVisualizer = ({ measurements, results }) => {
  const m = measurements;
  const statusOf = (key) => {
    const found = results?.checks?.find((c) => c.key === key);
    return found ? found.status : "empty";
  };

  // Combine camber + toe status per corner for the strongest signal.
  const cornerStatus = (camberKey, extraKeys = []) => {
    const keys = [camberKey, ...extraKeys];
    const stats = keys.map(statusOf).filter((s) => s !== "empty");
    if (!stats.length) return "empty";
    if (stats.includes("fail")) return "fail";
    if (stats.includes("warn")) return "warn";
    return "pass";
  };

  return (
    <div className="relative rounded-md bg-ink border border-steel p-4 overflow-hidden">
      <div className="absolute inset-0 tactical-grid opacity-40" />
      <svg viewBox="0 0 320 420" className="relative w-full max-w-[280px] mx-auto">
        {/* chassis frame */}
        <rect x={110} y={40} width={100} height={340} rx={14} fill="#121824" stroke="#2A364F" strokeWidth={2} />
        <line x1={160} y1={60} x2={160} y2={360} stroke="#2A364F" strokeWidth={2} strokeDasharray="6 6" />
        {/* axles */}
        <line x1={70} y1={110} x2={250} y2={110} stroke="#2A364F" strokeWidth={4} />
        <line x1={70} y1={310} x2={250} y2={310} stroke="#2A364F" strokeWidth={4} />

        <text x={160} y={28} textAnchor="middle" fill="#64748B" fontSize={11} fontFamily="'Share Tech Mono'" letterSpacing={3}>FRONT</text>
        <text x={160} y={405} textAnchor="middle" fill="#64748B" fontSize={11} fontFamily="'Share Tech Mono'" letterSpacing={3}>REAR</text>

        <Wheel x={70} y={110} side="left" camber={m.frontLeft?.camber} toe={m.frontToe} status={cornerStatus("fl-camber", ["fl-caster", "front-toe"])} />
        <Wheel x={250} y={110} side="right" camber={m.frontRight?.camber} toe={m.frontToe} status={cornerStatus("fr-camber", ["fr-caster", "front-toe"])} />
        <Wheel x={70} y={310} side="left" camber={m.rearLeft?.camber} toe={m.rearToe} status={cornerStatus("rl-camber", ["rear-toe"])} />
        <Wheel x={250} y={310} side="right" camber={m.rearRight?.camber} toe={m.rearToe} status={cornerStatus("rr-camber", ["rear-toe"])} />
      </svg>
      <p className="relative text-center font-mono text-[10px] text-slate-500 tracking-widest mt-2">
        LIVE CHASSIS TELEMETRY · TILT = CAMBER · ROTATION = TOE
      </p>
    </div>
  );
};
